import { resolveReadableColors } from "./ReadableColors";
/**
 * ConsentManager - Maps BannerConfig to CookieConsent.run() options
 *
 * This is the bridge between our API config shape and orestbida/cookieconsent v3's config shape.
 * See: https://cookieconsent.orestbida.com/reference/configuration-reference.html
 */

import * as CookieConsent from "vanilla-cookieconsent";
import type { BannerConfig, GeoInfo, CategoryConfig } from "./types";
import { EventReporter } from "./EventReporter";
import { GtagIntegration } from "./GtagIntegration";
import { AutoHideController } from "./AutoHideController";
import { buildSimpleAppearanceStyles, buildCompactAppearanceStyles } from "./SimpleAppearance";

const THEME_STYLE_ID = "consentpop-theme-styles";
const PREFERENCES_LAUNCHER_ID = "consentpop-preferences-launcher";
const AUTO_HIDE_MARKER_PREFIX = "cp_auto_hidden";
// Inline Lucide SVGs keep the embeddable widget framework-free while avoiding
// font-dependent emoji glyphs.
const LUCIDE_SVG_ATTRIBUTES =
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';
const CONSENTPOP_COOKIE_ICON = `<svg class="cp__icon cp__icon--brand" data-consentpop-logo="cookie" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><linearGradient id="cp-brand-cookie-bake" x1=".2" y1="0" x2=".8" y2="1" gradientUnits="objectBoundingBox"><stop offset="0" stop-color="#f8d47e"/><stop offset=".55" stop-color="#eab44f"/><stop offset="1" stop-color="#c58232"/></linearGradient></defs><path fill="url(#cp-brand-cookie-bake)" d="M32 3c3 0 6 1 9 2-1 5 3 10 8 10 0 6 5 10 10 10l2 7c0 16-13 29-29 29S3 48 3 32 16 3 32 3Z"/><g fill="#452a19"><circle cx="16" cy="19" r="2.5"/><circle cx="47" cy="44" r="2.5"/><circle cx="20" cy="48" r="2"/><rect x="20" y="28" width="5" height="8" rx="2.5"/><rect x="36" y="28" width="5" height="8" rx="2.5"/></g><path d="M26 42q5 5 10 0" fill="none" stroke="#452a19" stroke-width="3" stroke-linecap="round"/></svg>`;
const MANAGE_ICON = `<svg class="cp__icon" data-lucide="sliders-horizontal" ${LUCIDE_SVG_ATTRIBUTES}><path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-3M14 20H3M14 2v4M8 10v4M18 18v4" /></svg>`;
const LUCIDE_SETTINGS_ICON = `<svg class="cp__icon cp__icon--settings" data-lucide="settings" ${LUCIDE_SVG_ATTRIBUTES}><path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" /><circle cx="12" cy="12" r="3" /></svg>`;
const CONSENTPOP_ATTRIBUTION_FOOTER =
  `<a class="cp__attribution-link" href="https://consentpop.com" target="_blank" rel="noopener noreferrer" aria-label="Powered by ConsentPop"><span>Powered by </span>${CONSENTPOP_COOKIE_ICON}<span>ConsentPop</span></a>`;
// Hidden consent-modal SVG definitions must not capture the visible footer's fill.
const PREFERENCES_ATTRIBUTION_FOOTER = CONSENTPOP_ATTRIBUTION_FOOTER.replace(
  /cp-brand-cookie-bake/g, "cp-preferences-cookie-bake",
);
const WIDGET_LABEL_DEFAULTS = {
  launcherLabel: "Cookie settings",
  privacyPolicyLabel: "Privacy policy",
  cookiePolicyLabel: "Cookie policy",
  policyLinksLabel: "Site policies",
  compactPrefix: "We use",
  compactSettingsLabel: "cookies",
  compactAcceptLabel: "Accept all cookies and close",
} as const;
const CONSENT_COPY_FIELDS = ["title", "description", "acceptAllBtn", "acceptNecessaryBtn", "showPreferencesBtn"];
const PREFERENCES_COPY_FIELDS = ["title", "acceptAllBtn", "acceptNecessaryBtn", "savePreferencesBtn", "closeIconLabel"];
const PREFERENCE_SECTION_CATEGORIES = [undefined, "necessary", "analytics", "marketing", "functionality"];
const LOCALE_CODE = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function safeLocaleText(value: unknown, maxLength: number, multiline = false): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= maxLength &&
    !(multiline ? /[<>\u0000-\u0009\u000B-\u001F\u007F]/ : /[<>\u0000-\u001F\u007F]/).test(value);
}

function isCompleteLocale(value: unknown): boolean {
  if (!isRecord(value) || !isRecord(value.consentModal) || !isRecord(value.preferencesModal)) {
    return false;
  }
  const consentModal = value.consentModal;
  const preferencesModal = value.preferencesModal;
  if (!CONSENT_COPY_FIELDS.every((field) => safeLocaleText(consentModal[field],
    field === "description" ? 500 : 120, field === "description"))) return false;
  if (!PREFERENCES_COPY_FIELDS.every((field) => safeLocaleText(preferencesModal[field], 120))) {
    return false;
  }
  const sections = preferencesModal.sections;
  return Array.isArray(sections) && sections.length === PREFERENCE_SECTION_CATEGORIES.length &&
    sections.every((section, index) => isRecord(section) &&
      section.linkedCategory === PREFERENCE_SECTION_CATEGORIES[index] &&
      safeLocaleText(section.title, 120) && safeLocaleText(section.description, 500, true));
}

type ThemePreset = "simple";
type ConsentMode = "opt-in" | "opt-out";
type Rgb = { r: number; g: number; b: number };
type ThemeSurface = { background: string; text: string };
type ThemePalette = {
  primary: string;
  background: string;
  text: string;
  mutedText: string;
  primaryText: string;
  primaryHover: string;
  primaryBorder: string;
  secondaryText: string;
  secondaryBorder: string;
  border: string;
};
type ThemePresentation = {
  layout: "bar" | "box";
  position: string;
};

const DARK_THEME_SURFACES: Record<ThemePreset, ThemeSurface> = {
  simple: { background: "#172438", text: "#f0f5ff" },
};

export class ConsentManager {
  private config: BannerConfig;
  private geo: GeoInfo;
  private eventReporter: EventReporter;
  private gtagIntegration: GtagIntegration;
  private autoHideController: AutoHideController | null = null;
  private displayReported = false;

  constructor(
    config: BannerConfig,
    geo: GeoInfo,
    eventReporter: EventReporter,
    gtagIntegration: GtagIntegration,
  ) {
    this.config = { ...config, themePreset: "simple", layout: ["box", "compact"].includes(config.layout) ? config.layout : "bar" };
    this.geo = geo;
    this.eventReporter = eventReporter;
    this.gtagIntegration = gtagIntegration;
  }

  /**
   * Initialize cookieconsent with mapped configuration
   */
  async init(): Promise<void> {
    // Set default denied state for Google Consent Mode BEFORE cookieconsent runs
    if (this.config.googleConsentMode) {
      this.gtagIntegration.setDefaultDenied();
    }

    // Inject CSS variable overrides for theming
    this.injectThemeStyles();
    this.injectPreferencesLauncher();
    this.autoHideController = this.createAutoHideController();

    // Run cookieconsent with mapped config
    await CookieConsent.run(this.buildConfig(this.shouldSuppressAutoShow()));
  }

  /**
   * Build CookieConsent.run() config from our BannerConfig
   */
  private buildConfig(
    suppressAutoShow = false,
  ): Parameters<typeof CookieConsent.run>[0] {
    const resolvedMode = this.getResolvedMode();

    const translations = this.buildTranslations(resolvedMode);
    const languageCode = this.getResolvedLanguage();

    return {
      // Core behavior
      mode: resolvedMode,
      autoShow: this.config.autoShow && !suppressAutoShow,
      revision: this.config.revision || 0,
      disablePageInteraction: this.config.disablePageInteraction,
      manageScriptTags: true,
      autoClearCookies: true,

      // Cookie storage
      cookie: {
        name: this.config.cookieName || "cp_consent",
        expiresAfterDays: this.config.cookieExpiry || 365,
      },

      // Layout
      guiOptions: {
        consentModal: {
          layout: this.buildLayoutString() as any,
          position: this.buildPositionString() as any,
          flipButtons: this.config.flipButtons,
          equalWeightButtons: this.config.equalWeightButtons,
        },
        preferencesModal: {
          layout: "box",
          flipButtons: this.config.flipButtons,
          equalWeightButtons: this.config.equalWeightButtons,
        },
      },

      // Categories (pass through — our DB schema matches cookieconsent's shape)
      categories: this.buildCategories(),

      // Language / translations
      language: {
        default: languageCode,
        rtl: Object.keys(translations).filter((locale) => /^(ar|fa|he|ur)(-|$)/i.test(locale)),
        translations: translations as any,
      },

      // Callbacks
      onFirstConsent: ({ cookie }) => {
        this.autoHideController?.consented();
        this.handleConsent(cookie);
      },
      onChange: ({ cookie }) => {
        this.autoHideController?.consented();
        this.handleConsent(cookie);
      },
      onConsent: ({ cookie }) => {
        this.updateLauncherVisibility(
          cookie.categories,
          CookieConsent.getUserPreferences().acceptedServices,
        );
        // On every page load with existing consent — update gtag
        if (this.config.googleConsentMode) {
          this.gtagIntegration.updateFromCategories(cookie.categories);
        }
      },
      onModalReady: ({ modalName, modal }) => {
        if (modalName === "consentModal") {
          this.prepareConsentModal(modal);
        }
        if (modalName === "preferencesModal") {
          this.injectAttributionFooter(modal);
          this.injectPolicyLinks(modal, "preferencesModal");
        }
      },
      onModalShow: ({ modalName }) => {
        if (modalName === "consentModal") {
          this.handleConsentModalShow();
        }
        if (modalName === "preferencesModal") {
          this.autoHideController?.engage();
          this.injectAttributionFooter(
            document.querySelector("#cc-main .pm") as HTMLElement | null,
          );
          this.injectPolicyLinks(
            document.querySelector("#cc-main .pm") as HTMLElement | null,
            "preferencesModal",
          );
        }
      },
      onModalHide: ({ modalName }) => {
        if (modalName === "consentModal") {
          this.autoHideController?.destroy();
        }
      },
    };
  }

  private createAutoHideController(): AutoHideController | null {
    if (!this.isAutoHideEnabled()) return null;

    return new AutoHideController(
      {
        mode: this.config.autoHideMode,
        delaySeconds: this.config.autoHideDelaySeconds,
        scrollPercent: this.config.autoHideScrollPercent,
      },
      {
        hide: () => CookieConsent.hide(),
        reportAutoHide: (trigger) => {
          this.writeAutoHideMarker();
          this.eventReporter.reportInteraction({
            event: "BANNER_AUTO_HIDDEN",
            trigger,
            configVersion: this.config.configVersion,
            revision: this.config.revision,
          });
        },
      },
    );
  }

  private handleConsentModalShow(): void {
    if (!this.displayReported) {
      this.displayReported = true;
      this.eventReporter.reportInteraction({
        event: "BANNER_DISPLAYED",
        configVersion: this.config.configVersion,
        revision: this.config.revision,
      });
    }
    this.autoHideController?.arm();
  }

  private isAutoHideEnabled(): boolean {
    return this.config.autoHideMode === "timeout" || this.config.autoHideMode === "scroll";
  }

  private autoHideMarkerKey(): string {
    return `${AUTO_HIDE_MARKER_PREFIX}:${this.eventReporter.getApiKey()}`;
  }

  private shouldSuppressAutoShow(): boolean {
    if (!this.isAutoHideEnabled()) return false;

    try {
      const raw = sessionStorage.getItem(this.autoHideMarkerKey());
      if (!raw) return false;
      const marker = JSON.parse(raw) as { configVersion?: unknown; revision?: unknown };
      return (
        marker.configVersion === this.config.configVersion &&
        marker.revision === this.config.revision
      );
    } catch {
      return false;
    }
  }

  private writeAutoHideMarker(): void {
    try {
      sessionStorage.setItem(
        this.autoHideMarkerKey(),
        JSON.stringify({
          configVersion: this.config.configVersion,
          revision: this.config.revision,
          hiddenAt: Date.now(),
        }),
      );
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  /** Keep CookieConsent's own buttons and handlers so consent and preferences stay shared. */
  private prepareCompactModal(modal: HTMLElement): void {
    const title = modal.querySelector<HTMLElement>(".cm__title");
    const manage = modal.querySelector<HTMLButtonElement>('.cm__btn[data-role="show"]');
    const accept = modal.querySelector<HTMLButtonElement>('.cm__btn[data-role="all"]');
    if (!title || !manage || !accept) return;
    const labels = this.getWidgetLabels();
    manage.textContent = labels.compactSettingsLabel;
    manage.setAttribute("aria-label", labels.launcherLabel);
    title.replaceChildren(document.createTextNode(`${labels.compactPrefix} `), manage);
    title.setAttribute("aria-label", `${labels.compactPrefix} ${labels.compactSettingsLabel}`);
    accept.innerHTML = `<svg ${LUCIDE_SVG_ATTRIBUTES}><path d="m6 6 12 12M6 18 18 6" /></svg>`;
    accept.setAttribute("aria-label", labels.compactAcceptLabel);
    accept.title = labels.compactAcceptLabel;
    modal.appendChild(accept);
    modal.querySelector('.cm__btn[data-role="necessary"]')?.remove();
  }

  private prepareConsentModal(modal: HTMLElement): void {
    if (this.config.layout === "compact") this.prepareCompactModal(modal);
    const manage = modal.querySelector<HTMLButtonElement>('.cm__btn[data-role="show"]');
    const footer = modal.querySelector('.cm__footer');
    if (this.config.layout !== "compact" && manage && footer && !manage.querySelector('[data-lucide="sliders-horizontal"]')) {
      const label = manage.textContent?.trim() || "Manage preferences";
      manage.setAttribute("aria-label", label);
      manage.title = label;
      manage.innerHTML = MANAGE_ICON;
      const text = document.createElement("span");
      text.textContent = label === "Manage preferences" ? "Manage" : label;
      manage.appendChild(text);
      footer.prepend(manage);
    }

    if (!this.isAutoHideEnabled()) return;

    const engage = () => this.autoHideController?.engage();
    modal.addEventListener("pointerenter", engage, { once: true });
    modal.addEventListener("pointerdown", engage, { once: true });
    modal.addEventListener("focusin", (event) => {
      if (event.target instanceof HTMLElement && event.target.matches('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])')) engage();
    });
    modal.addEventListener("keydown", engage, { once: true });
    modal.addEventListener("touchstart", engage, { once: true });
  }

  private injectPreferencesLauncher(): void {
    const launcherMode = this.config.preferencesLauncher ?? "hidden";
    const shouldShowLauncher = launcherMode === "always" ||
      (launcherMode === "auto" && this.isAutoHideEnabled());
    if (!shouldShowLauncher || document.getElementById(PREFERENCES_LAUNCHER_ID)) {
      return;
    }

    const launcher = document.createElement("button");
    launcher.id = PREFERENCES_LAUNCHER_ID;
    launcher.type = "button";
    launcher.className = "cp__preferences-launcher";
    const launcherLabel = this.getWidgetLabels().launcherLabel;
    launcher.setAttribute("aria-label", launcherLabel);
    launcher.dataset.style = this.config.preferencesLauncherStyle === "mini"
      ? "mini"
      : "labeled";
    launcher.dataset.position = [
      "top-left",
      "top-right",
      "bottom-left",
      "bottom-right",
    ].includes(this.config.preferencesLauncherPosition ?? "")
      ? this.config.preferencesLauncherPosition!
      : "bottom-right";
    const icon = document.createElement("span");
    icon.className = "cp__preferences-launcher-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = LUCIDE_SETTINGS_ICON;
    const label = document.createElement("span");
    label.className = "cp__preferences-launcher-label";
    label.textContent = launcherLabel;
    launcher.append(icon, label);
    launcher.addEventListener("click", () => this.showPreferences());
    document.body.appendChild(launcher);
  }

  private updateLauncherVisibility(
    acceptedCategories: string[],
    acceptedServices: Record<string, string[]> = {},
  ): void {
    const launcher = document.getElementById(PREFERENCES_LAUNCHER_ID);
    if (!launcher) return;
    const acceptedAllCategories = Object.keys(this.config.categories)
      .every((category) => acceptedCategories.includes(category));
    const acceptedAllServices = Object.entries(this.config.categories)
      .every(([category, config]) => Object.keys(config.services ?? {})
        .every((service) => acceptedServices[category]?.includes(service)));
    launcher.hidden = this.config.hideAfterAcceptAll === true &&
      acceptedAllCategories && acceptedAllServices;
  }

  /** Open the preferences dialog through the supported widget API. */
  public showPreferences(): void {
    this.autoHideController?.engage();
    CookieConsent.showPreferences();
    this.eventReporter.reportInteraction({
      event: "PREFERENCES_REOPENED",
      configVersion: this.config.configVersion,
      revision: this.config.revision,
    });
  }

  /**
   * Handle consent action — report event + update gtag
   */
  private handleConsent(cookie: {
    categories: string[];
    revision: number;
  }): void {
    const prefs = CookieConsent.getUserPreferences();
    this.updateLauncherVisibility(cookie.categories, prefs.acceptedServices);
    const acceptType = prefs.acceptType; // 'all' | 'necessary' | 'custom'

    let consentType: "ACCEPT_ALL" | "REJECT_ALL" | "CUSTOM";
    if (acceptType === "all") {
      consentType = "ACCEPT_ALL";
    } else if (acceptType === "necessary") {
      consentType = "REJECT_ALL";
    } else {
      consentType = "CUSTOM";
    }

    // Update Google Consent Mode
    let gtagConsent: Record<string, string> | undefined;
    if (this.config.googleConsentMode) {
      gtagConsent = this.gtagIntegration.updateFromCategories(
        cookie.categories,
      );
    }

    // Fire-and-forget consent event to API
    this.eventReporter.report({
      consentType,
      acceptedCategories: prefs.acceptedCategories,
      rejectedCategories: prefs.rejectedCategories,
      acceptedServices: prefs.acceptedServices,
      rejectedServices: prefs.rejectedServices,
      revision: cookie.revision,
      gtagConsent,
    });
  }

  /**
   * Build cookieconsent layout string (e.g., "bar inline", "box wide")
   */
  private buildLayoutString(): string {
    const { layout } = this.getThemePresentation();
    if (layout === "bar") {
      return "bar inline";
    }
    return layout;
  }

  /**
   * Build cookieconsent position string (e.g., "bottom center", "top right")
   */
  private buildPositionString(): string {
    return this.getThemePresentation().position;
  }

  /** Box and bar share one appearance, including legacy saved configurations. */
  private getThemePresentation(): ThemePresentation {
    const layout = this.config.layout === "bar" ? "bar" : "box";
    const alignment = layout === "box" && ["left", "center", "right"].includes(this.config.alignment)
      ? this.config.alignment : layout === "box" ? "right" : "center";
    return { layout, position: `bottom ${alignment}` };
  }

  private getThemePreset(): ThemePreset {
    return "simple";
  }

  private getResolvedMode(): ConsentMode {
    return (this.geo.resolvedMode || this.config.mode) === "opt-out"
      ? "opt-out"
      : "opt-in";
  }

  private getPalette(): ThemePalette {
    const darkSurface = DARK_THEME_SURFACES[this.getThemePreset()];
    const primary = this.config.primaryColor;
    const background =
      this.config.darkMode &&
      this.isDefaultLightBackground(this.config.backgroundColor)
        ? darkSurface.background
        : this.config.backgroundColor;
    const requestedText =
      this.config.darkMode && this.isDefaultDarkText(this.config.textColor)
        ? darkSurface.text
        : this.config.textColor;
    const colors = resolveReadableColors(primary, background, requestedText);
    return {
      ...colors,
      border: `color-mix(in srgb, ${colors.primary} 28%, ${colors.background})`,
      secondaryText: colors.text,
      secondaryBorder: colors.primaryBorder,
    };
  }

  private getContrastColor(color: string): "#07111f" | "#ffffff" {
    const dark = "#07111f";
    const light = "#ffffff";
    return this.getContrastRatio(dark, color) >=
      this.getContrastRatio(light, color)
      ? dark
      : light;
  }

  private hasReadableContrast(
    foreground: string,
    background: string,
    minimum: number,
  ): boolean {
    return this.getContrastRatio(foreground, background) >= minimum;
  }

  private getContrastRatio(foreground: string, background: string): number {
    const fg = this.hexToRgb(foreground);
    const bg = this.hexToRgb(background);
    if (!fg || !bg) return 21;

    const lighter = Math.max(
      this.getRelativeLuminance(fg),
      this.getRelativeLuminance(bg),
    );
    const darker = Math.min(
      this.getRelativeLuminance(fg),
      this.getRelativeLuminance(bg),
    );

    return (lighter + 0.05) / (darker + 0.05);
  }

  private getRelativeLuminance({ r, g, b }: Rgb): number {
    const [red, green, blue] = [r, g, b].map((channel) => {
      const value = channel / 255;
      return value <= 0.03928
        ? value / 12.92
        : Math.pow((value + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  }

  private hexToRgb(color: string): Rgb | null {
    const trimmed = color.trim();
    const short = /^#([0-9a-fA-F]{3})$/.exec(trimmed);
    if (short) {
      const [r, g, b] = short[1]
        .split("")
        .map((value) => parseInt(value + value, 16));
      return { r, g, b };
    }

    const full = /^#([0-9a-fA-F]{6})/.exec(trimmed);
    if (!full) return null;

    const hex = full[1];
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  private isDefaultLightBackground(color: string): boolean {
    return ["#ffffff", "#fffdf7"].includes(color.toLowerCase());
  }

  private isDefaultDarkText(color: string): boolean {
    return ["#111827", "#1f2937", "#111111", "#0f0f0f"].includes(
      color.toLowerCase(),
    );
  }

  private injectAttributionFooter(modal: HTMLElement | null): void {
    if (!modal || modal.querySelector(".cp__attribution")) return;

    const attribution = document.createElement("div");
    attribution.className = "cp__attribution";

    const link = document.createElement("a");
    link.href = "https://consentpop.com";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.className = "cp__attribution-link";
    link.setAttribute("aria-label", "Powered by ConsentPop");
    link.innerHTML = `<span>Powered by </span>${CONSENTPOP_COOKIE_ICON.replace(/cp-brand-cookie-bake/g, "cp-preferences-cookie-bake")}<span>ConsentPop</span>`;

    attribution.appendChild(link);

    const footer = modal.querySelector(".pm__footer");
    if (footer) {
      footer.insertAdjacentElement("afterend", attribution);
      return;
    }

    modal.appendChild(attribution);
  }

  private injectPolicyLinks(
    modal: HTMLElement | null,
    modalName: "consentModal" | "preferencesModal",
  ): void {
    if (!modal || modal.querySelector(".cp__policy-links")) return;

    const labels = this.getWidgetLabels();

    const links = [
      { url: this.config.privacyPolicyUrl, label: labels.privacyPolicyLabel },
      { url: this.config.cookiePolicyUrl, label: labels.cookiePolicyLabel },
    ].flatMap(({ url, label }) => {
      if (!url) return [];
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== "https:" || !parsed.hostname || parsed.username || parsed.password) {
          return [];
        }
        return [{ href: parsed.href, label }];
      } catch {
        return [];
      }
    });
    if (links.length === 0) return;

    const nav = document.createElement("nav");
    nav.className = "cp__policy-links";
    nav.setAttribute("aria-label", labels.policyLinksLabel);
    for (const { href, label } of links) {
      const link = document.createElement("a");
      link.href = href;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = label;
      nav.appendChild(link);
    }

    const footer = modal.querySelector(
      modalName === "consentModal" ? ".cm__footer" : ".pm__footer",
    );
    if (modalName === "consentModal" && footer) {
      footer.prepend(nav);
    } else if (footer) {
      footer.insertAdjacentElement("afterend", nav);
    } else {
      modal.appendChild(nav);
    }
  }

  /**
   * Build categories object for cookieconsent
   */
  private buildCategories(): Record<
    string,
    {
      enabled?: boolean;
      readOnly?: boolean;
      services?: Record<string, {
        label?: string;
        cookies?: Array<{ name: string | RegExp; path?: string; domain?: string }>;
      }>;
    }
  > {
    const result: Record<
      string,
      {
        enabled?: boolean;
        readOnly?: boolean;
        services?: Record<string, {
          label?: string;
          cookies?: Array<{ name: string | RegExp; path?: string; domain?: string }>;
        }>;
      }
    > = {};

    for (const [key, cat] of Object.entries(
      this.config.categories as Record<string, CategoryConfig>,
    )) {
      result[key] = {
        enabled: cat.enabled ?? key === "necessary",
        readOnly: cat.readOnly ?? key === "necessary",
      };
      if (cat.services) {
        result[key].services = {};
        for (const [svcKey, svc] of Object.entries(cat.services)) {
          result[key].services![svcKey] = {
            label: svc.label,
            cookies: svc.cookies,
          };
        }
      }
    }

    return result;
  }

  private getCompleteLocaleTranslations(): Record<string, {
    consentModal: Record<string, string>;
    preferencesModal: Record<string, unknown>;
  }> {
    const stored = isRecord(this.config.translations) ? this.config.translations : {};
    const result: Record<string, {
      consentModal: Record<string, string>;
      preferencesModal: Record<string, unknown>;
    }> = {};
    for (const [locale, value] of Object.entries(stored)) {
      if (locale === "en" || !LOCALE_CODE.test(locale) || !isCompleteLocale(value)) continue;
      const translation = value as Record<string, Record<string, unknown>>;
      const consentModal = translation.consentModal;
      const preferencesModal = translation.preferencesModal;
      result[locale] = {
        consentModal: {
          ...Object.fromEntries(CONSENT_COPY_FIELDS.map((field) => [field, consentModal[field] as string])),
          footer: CONSENTPOP_ATTRIBUTION_FOOTER,
        },
        preferencesModal: {
          ...Object.fromEntries(PREFERENCES_COPY_FIELDS.map((field) => [field, preferencesModal[field]])),
          sections: (preferencesModal.sections as Record<string, unknown>[]).map((section) => ({
            title: section.title,
            description: section.description,
            ...(section.linkedCategory ? { linkedCategory: section.linkedCategory } : {}),
          })),
          footer: PREFERENCES_ATTRIBUTION_FOOTER,
        },
      };
    }
    return result;
  }

  private getResolvedLanguage(): string {
    const completeLocales = this.getCompleteLocaleTranslations();
    const available = new Map<string, string>([
      ["en", "en"],
      ...Object.keys(completeLocales).map((locale): [string, string] => [locale.toLowerCase(), locale]),
    ]);
    const configured = available.get((this.config.defaultLanguage || "en").toLowerCase()) ?? "en";
    if (!this.config.autoDetectLanguage || typeof navigator === "undefined") return configured;
    const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const browserLanguage of browserLanguages) {
      const normalized = browserLanguage.toLowerCase();
      const exact = available.get(normalized);
      if (exact) return exact;
      const base = available.get(normalized.split("-")[0]);
      if (base) return base;
    }
    return configured;
  }

  private getWidgetLabels(): Record<keyof typeof WIDGET_LABEL_DEFAULTS, string> {
    const locale = this.getResolvedLanguage();
    const stored = isRecord(this.config.translations) ? this.config.translations : {};
    const translation = isRecord(stored[locale]) ? stored[locale] : {};
    const ui = isRecord(translation.ui) ? translation.ui : {};
    const localizedPreferences = locale === "en" ? null : this.getCompleteLocaleTranslations()[locale];
    const defaults = {
      ...WIDGET_LABEL_DEFAULTS,
      launcherLabel: (localizedPreferences?.preferencesModal.title as string | undefined) ??
        WIDGET_LABEL_DEFAULTS.launcherLabel,
    };
    return Object.fromEntries(Object.entries(defaults).map(([field, fallback]) => [
      field,
      safeLocaleText(ui[field], 120) ? ui[field] : fallback,
    ])) as Record<keyof typeof WIDGET_LABEL_DEFAULTS, string>;
  }

  /**
   * Build translations object with fallback to English defaults
   */
  private buildTranslations(
    resolvedMode: ConsentMode,
  ): Record<string, unknown> {
    // Themes only change presentation. The same neutral choice copy is used
    // everywhere so a visual preset never implies that simply browsing means
    // a visitor has consented.
    const simple = this.getThemePreset() === "simple";
    const consentModalCopy = {
      title: simple ? "Your privacy, your choice." : "We use cookies",
      description: simple
        ? "We use cookies to keep this site working, measure traffic, and personalize your experience. Choose which optional cookies to allow."
        : "Choose what this site can use. You can update consent any time.",
      acceptAllBtn: "Accept all",
      acceptNecessaryBtn: simple ? "Reject optional" : "Reject all",
      showPreferencesBtn: simple ? "Manage preferences" : "Manage choices",
    };

    const isOptOut = resolvedMode === "opt-out";
    const defaultConsentModal = {
      ...consentModalCopy,
      description: `${consentModalCopy.description}${
        isOptOut ? " Non-essential cookies may be used unless you opt out." : ""
      }`,
      footer: CONSENTPOP_ATTRIBUTION_FOOTER,
    };

    const defaultEn = {
      consentModal: {
        ...defaultConsentModal,
      },
      preferencesModal: {
        title: "Cookie Preferences",
        acceptAllBtn: simple ? "Accept all" : "Accept All",
        acceptNecessaryBtn: simple ? "Reject optional" : "Reject All",
        savePreferencesBtn: simple ? "Save preferences" : "Save Preferences",
        closeIconLabel: "Close",
        footer: PREFERENCES_ATTRIBUTION_FOOTER,
        sections: [
          {
            title: "Cookie Usage",
            description:
              "We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want.",
          },
          {
            title: "Strictly Necessary",
            description:
              "These cookies are essential for the proper functioning of the website. Without these cookies, the website would not work properly.",
            linkedCategory: "necessary",
          },
          {
            title: "Analytics",
            description:
              "These cookies help the site understand how visitors use pages and features.",
            linkedCategory: "analytics",
          },
          {
            title: "Marketing",
            description:
              "These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.",
            linkedCategory: "marketing",
          },
          {
            title: "Functionality",
            description:
              "These cookies allow the website to remember choices you make and provide enhanced, more personal features.",
            linkedCategory: "functionality",
          },
        ],
      },
    };

    const storedTranslations = isRecord(this.config.translations) ? this.config.translations : {};
    const translations = this.getCompleteLocaleTranslations();
    const hasCustomEn =
      isRecord(storedTranslations["en"]);

    if (hasCustomEn) {
      const customEn = storedTranslations["en"] as Record<string, any>;
      const { ui: _widgetLabels, ...cookieConsentEn } = customEn;
      const customConsentModal = customEn.consentModal || {};
      const customPreferencesModal = customEn.preferencesModal || {};

      return {
        ...translations,
        en: {
          ...defaultEn,
          ...cookieConsentEn,
          consentModal: {
            ...defaultEn.consentModal,
            ...customConsentModal,
            footer: customConsentModal.footer ?? CONSENTPOP_ATTRIBUTION_FOOTER,
          },
          preferencesModal: {
            ...defaultEn.preferencesModal,
            ...customPreferencesModal,
            footer:
              customPreferencesModal.footer ?? CONSENTPOP_ATTRIBUTION_FOOTER,
          },
        },
      };
    }

    return {
      ...translations,
      en: defaultEn,
    };
  }

  /**
   * Inject CSS variable overrides for theming
   */
  private injectThemeStyles(): void {
    const preset = this.getThemePreset();
    const resolvedMode = this.getResolvedMode();
    const palette = this.getPalette();
    document.documentElement.dataset.consentpopTheme = preset;
    document.documentElement.dataset.consentpopLayout = this.config.layout;
    document.documentElement.dataset.consentpopMode = resolvedMode;
    document.documentElement.dataset.consentpopDensity =
      this.config.bannerDensity === "mini" ? "mini" : this.config.bannerDensity === "compact" ? "compact" : "standard";

    if (this.config.darkMode) {
      document.documentElement.classList.add("cc--darkmode");
    }

    const buttonRadius = this.config.buttonStyle === "sharp" ? "0" : "14px";

    const modalRadius = this.config.buttonStyle === "sharp" ? "2px" : "20px";

    let style = document.getElementById(
      THEME_STYLE_ID,
    ) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = THEME_STYLE_ID;
      document.head.appendChild(style);
    }

    style.textContent = `
      :root {
        --cc-font-family: inherit;
        --cc-modal-border-radius: ${modalRadius};
        --cc-btn-border-radius: ${buttonRadius};
        --cc-modal-margin: 20px;
        --cc-btn-primary-bg: ${palette.primary};
        --cc-btn-primary-hover-bg: ${palette.primaryHover};
        --cc-btn-primary-border-color: ${palette.primaryBorder};
        --cc-btn-primary-color: ${palette.primaryText};
        --cc-btn-secondary-bg: transparent;
        --cc-btn-secondary-color: ${palette.secondaryText};
        --cc-btn-secondary-border-color: ${palette.secondaryBorder};
        --cc-btn-secondary-hover-bg: ${palette.background};
        --cc-btn-secondary-hover-color: ${palette.secondaryText};
        --cc-btn-secondary-hover-border-color: ${palette.secondaryBorder};
        --cc-toggle-on-bg: ${palette.primary};
        --cc-toggle-off-bg: color-mix(in srgb, ${palette.text} 32%, ${palette.background});
        --cc-toggle-readonly-bg: color-mix(in srgb, ${palette.text} 18%, ${palette.background});
        --cc-link-color: ${palette.text};
        --cc-bg: ${palette.background};
        --cc-primary-color: ${palette.text};
        --cc-secondary-color: ${palette.mutedText};
        --cc-separator-border-color: ${palette.border};
        --cc-cookie-category-block-bg: ${palette.background};
        --cc-cookie-category-block-border: color-mix(in srgb, ${palette.primary} 12%, transparent);
        --cc-cookie-category-block-hover-bg: ${palette.background};
        --cc-footer-bg: ${palette.background};
      }
      #cc-main {
        letter-spacing: 0;
      }
      #cc-main .cm,
      #cc-main .pm {
        border: 1px solid color-mix(in srgb, ${palette.text} 12%, transparent);
        box-shadow:
          0 24px 64px rgba(15, 15, 12, 0.16),
          0 4px 14px rgba(15, 15, 12, 0.06);
      }
      #cc-main .cm {
        border-radius: var(--cc-modal-border-radius);
        max-width: min(340px, calc(100vw - 32px));
      }
      #cc-main .cm.cm--bar,
      #cc-main .cm.cm--box,
      #cc-main .cm.cm--cloud {
        border-radius: var(--cc-modal-border-radius);
      }
      #cc-main .cm--bar {
        left: 50%;
        right: auto;
        width: min(820px, calc(100vw - 40px));
        max-width: min(820px, calc(100vw - 40px));
        overflow: hidden;
        transform: translate(-50%, 26px);
      }
      #cc-main .cm--bar.cm--top {
        top: 20px;
        transform: translate(-50%, -100%);
      }
      #cc-main .cm--bar.cm--bottom {
        bottom: 20px;
        transform: translate(-50%, 100%);
      }
      .show--consent #cc-main .cc--anim .cm--bar {
        transform: translate(-50%, 0);
      }
      #cc-main .cm--box {
        width: min(380px, calc(100vw - 32px));
        max-width: min(380px, calc(100vw - 32px));
        overflow: hidden;
      }
      #cc-main .cm--box.cm--bottom {
        bottom: 20px;
        transform: translateY(100%);
      }
      #cc-main .cm--box.cm--bottom.cm--left {
        left: 20px;
        right: auto;
      }
      #cc-main .cm--box.cm--bottom.cm--right {
        right: 20px;
        left: auto;
      }
      #cc-main .cm--box.cm--bottom.cm--center {
        left: 50%;
        right: auto;
        transform: translate(-50%, 100%);
      }
      .show--consent #cc-main .cc--anim .cm--box {
        transform: translateY(0);
      }
      .show--consent #cc-main .cc--anim .cm--box.cm--center {
        transform: translate(-50%, 0);
      }
      #cc-main .cm__body {
        flex-direction: column;
        overflow: hidden;
        width: 100%;
        max-width: none;
        padding: 0;
      }
      #cc-main .cm--bar .cm__body,
      #cc-main .cm--bar.cm--inline .cm__body,
      #cc-main .cm--cloud .cm__body {
        align-items: center;
        flex-direction: row;
        gap: 18px;
        width: 100%;
        max-width: none;
        padding: 18px 20px 8px;
      }
      #cc-main .cm--box .cm__body {
        flex-direction: column;
        width: 100%;
        max-width: none;
        padding: 0;
      }
      #cc-main .cm__texts {
        flex: none;
        padding: 22px 22px 4px;
      }
      #cc-main .cm__title {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0;
        color: ${palette.text};
        font-size: 19px;
        font-weight: 800;
        letter-spacing: 0;
        line-height: 1.18;
      }
      #cc-main .cm__desc {
        max-height: none;
        margin-top: 12px;
        padding: 0;
        color: ${palette.mutedText};
        font-size: 14px;
        line-height: 1.45;
      }
      #cc-main .cm__btns {
        display: flex;
        flex-direction: column;
        gap: 10px !important;
        align-self: stretch;
        justify-content: stretch;
        box-sizing: border-box;
        width: 100% !important;
        max-width: none !important;
        border-top: 0 !important;
        padding: 18px 22px 22px;
      }
      #cc-main .cm--bar .cm__texts {
        flex: 1 1 auto;
        min-width: 0;
        padding: 0;
      }
      #cc-main .cm--bar .cm__btns {
        align-self: center;
        flex: 0 0 260px;
        gap: 10px !important;
        padding: 0;
        width: 260px !important;
      }
      #cc-main .cm--bar .cm__btn {
        height: 46px;
        min-height: 46px;
      }
      #cc-main .cm--box .cm__btns {
        padding: 18px 22px 22px;
      }
      #cc-main .cm__btn-group {
        display: grid;
        gap: 10px;
        grid-auto-columns: minmax(0, 1fr);
        grid-auto-flow: column;
        min-width: 0;
        width: 100%;
      }
      #cc-main .pm__footer {
        gap: 10px;
      }
      #cc-main .pm__btn-group {
        display: flex;
        flex: 1 1 0;
        gap: 10px;
        min-width: 0;
      }
      #cc-main .cm__btn + .cm__btn,
      #cc-main .cm__btn-group + .cm__btn-group,
      #cc-main .pm__btn + .pm__btn,
      #cc-main .pm__btn-group + .pm__btn-group {
        margin: 0 !important;
      }
      #cc-main .cm__btn[data-role="show"] {
        align-self: center;
        justify-self: center;
        order: 3;
        width: auto !important;
        min-height: 44px;
        padding: 6px 8px;
        border: 0;
        background: transparent !important;
        color: ${palette.mutedText};
        font-size: 12px;
        font-weight: 650;
      }
      #cc-main .cm__btn[data-role="all"] {
        order: 2;
      }
      #cc-main .cm__btn[data-role="necessary"] {
        order: 1;
      }
      #cc-main .cm__btn,
      #cc-main .pm__btn {
        box-sizing: border-box;
        margin: 0 !important;
        width: 100% !important;
        min-height: 44px;
        border-width: 1px;
        padding: 0 12px;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0;
        white-space: nowrap;
        transition:
          transform 150ms ease,
          box-shadow 150ms ease,
          background-color 150ms ease,
          border-color 150ms ease,
          color 150ms ease;
      }
      #cc-main .cm__btn-group .cm__btn,
      #cc-main .pm__btn-group .pm__btn {
        flex: 1 1 0;
        min-width: 0;
        width: auto !important;
      }
      #cc-main .cm__btn:not(.cm__btn--secondary),
      #cc-main .pm__btn:not(.pm__btn--secondary) {
        box-shadow: none;
        color: ${palette.primaryText};
      }
      #cc-main .cm--bar .cm__btn,
      #cc-main .cm--box.cm--wide .cm__btn,
      #cc-main .cm--cloud .cm__btn {
        min-width: 0;
      }
      #cc-main .cm__btn:hover,
      #cc-main .pm__btn:hover {
        transform: none;
      }
      #cc-main .cm__btn:active,
      #cc-main .pm__btn:active {
        transform: translateY(0);
      }
      #cc-main .cm__btn--close,
      #cc-main .pm__close-btn {
        align-items: center;
        background: transparent !important;
        border-color: color-mix(in srgb, ${palette.text} 14%, transparent) !important;
        box-shadow: none;
        color: ${palette.text} !important;
        display: flex;
        justify-content: center;
      }
      #cc-main .cm__btn.cm__btn--close {
        height: 44px;
        min-height: 44px;
        min-width: 44px !important;
        padding: 0 !important;
        width: 44px !important;
      }
      #cc-main .cm__btn--close span,
      #cc-main .pm__close-btn span {
        align-items: center;
        display: flex;
        height: 100%;
        justify-content: center;
        width: 100%;
      }
      #cc-main .cm__btn--close svg,
      #cc-main .cm__btn--close.cm__btn--secondary svg,
      #cc-main .pm__close-btn svg {
        stroke: ${palette.text} !important;
      }
      #cc-main .cm__btn--close:hover,
      #cc-main .pm__close-btn:hover {
        background: color-mix(in srgb, ${palette.text} 14%, ${palette.background}) !important;
        border-color: color-mix(in srgb, ${palette.text} 30%, transparent) !important;
      }
      #cc-main .cm__btn--close:hover svg,
      #cc-main .cm__btn--close.cm__btn--secondary:hover svg,
      #cc-main .pm__close-btn:hover svg {
        stroke: ${palette.text} !important;
      }
      #cc-main .cm__btn--secondary,
      #cc-main .pm__btn--secondary,
      #cc-main .pm__btn[data-role="necessary"] {
        background: transparent;
        border-color: color-mix(in srgb, ${palette.text} 18%, transparent);
        box-shadow: none;
        color: ${palette.mutedText};
      }
      #cc-main .cm__btn--secondary:hover,
      #cc-main .pm__btn--secondary:hover,
      #cc-main .pm__btn[data-role="necessary"]:hover {
        background: color-mix(in srgb, ${palette.text} 6%, transparent);
        border-color: color-mix(in srgb, ${palette.text} 28%, transparent);
        color: ${palette.text};
      }
      #cc-main .cm__footer,
      #cc-main .cp__attribution {
        background: transparent;
        border-top: none;
        color: ${palette.mutedText};
        text-align: center;
      }
      #cc-main .cm__footer {
        padding: 0 22px 12px;
      }
      #cc-main .cm--bar .cm__footer {
        padding: 0 20px 12px;
      }
      #cc-main .cp__attribution {
        padding: 8px 18px 10px;
      }
      #cc-main .cp__policy-links {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 6px 16px;
        padding: 8px 18px;
        color: ${palette.mutedText};
        text-align: center;
      }
      #cc-main .cm__footer .cp__policy-links {
        padding: 0 0 8px;
      }
      #cc-main .cp__policy-links a,
      #cc-main .cm__footer .cp__policy-links a {
        color: inherit;
        font-size: 11px;
        font-weight: 650;
        line-height: 1.4;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      #cc-main .cp__policy-links a:hover,
      #cc-main .cp__policy-links a:focus-visible {
        color: var(--cc-link-color);
      }
      #cc-main .cm__links,
      #cc-main .cp__attribution {
        display: flex;
        justify-content: center;
      }
      #cc-main .cm__links,
      #cc-main .cm__link-group {
        display: flex;
        justify-content: center;
        padding: 0;
      }
      #cc-main .cm__footer a,
      #cc-main .cp__attribution a {
        color: inherit;
        font-size: 10px;
        font-weight: 650;
        letter-spacing: 0;
        line-height: 1.2;
        text-decoration: none;
      }
      #cc-main .cp__attribution-link {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background-image: none;
        text-decoration: none;
      }
      #cc-main .cp__attribution-link:hover {
        background-image: none;
        text-decoration: none;
        font-weight: 900;
      }
      #cc-main .cp__icon {
        display: block;
        flex: 0 0 auto;
        width: 12px;
        height: 12px;
      }
      #cc-main .cp__icon--brand {
        width: 16px;
        height: 16px;
        transform-origin: center;
        transition: transform 180ms ease;
      }
      #cc-main .cp__attribution-link:hover .cp__icon--brand {
        transform: rotate(-12deg);
      }
      @media (prefers-reduced-motion: reduce) {
        #cc-main .cp__icon--brand { transition: none; }
      }
      #cc-main .cm__footer a:hover,
      #cc-main .cp__attribution a:hover {
        color: var(--cc-link-color);
        font-weight: 900;
        text-decoration: none;
      }
      #cc-main .pm__header {
        position: relative;
        min-height: 62px;
        padding: 14px 18px;
        border-bottom-color: color-mix(in srgb, ${palette.text} 10%, transparent);
        background: ${palette.background};
      }
      #cc-main .pm__title {
        gap: 9px;
        margin-right: 16px;
        color: ${palette.text};
        font-size: 17px;
        font-weight: 750;
        letter-spacing: -0.015em;
        line-height: 1.15;
      }
      #cc-main .cm__title::before {
        content: "";
        display: inline-block;
        width: 1.1em;
        height: 1.1em;
        margin-inline-end: .4em;
        vertical-align: -.15em;
        background: url("data:image/svg+xml,${encodeURIComponent(CONSENTPOP_COOKIE_ICON)}") center / contain no-repeat;
      }
      #cc-main .pm__title::before {
        content: "";
        width: 21px;
        height: 21px;
        flex: 0 0 auto;
        background: url("data:image/svg+xml,${encodeURIComponent(CONSENTPOP_COOKIE_ICON)}") center / contain no-repeat;
      }
      #cc-main .pm__body {
        padding: 16px 18px 18px;
        background: ${palette.background};
        scroll-padding: 16px;
      }
      #cc-main .pm__section {
        color: ${palette.text};
      }
      #cc-main .pm__section:first-child {
        margin-bottom: 12px;
        padding: 2px 2px 12px;
        border: 0;
        border-bottom: 1px solid color-mix(in srgb, ${palette.text} 9%, transparent);
        border-radius: 0;
        background: transparent;
      }
      #cc-main .pm__section:first-child .pm__section-title {
        font-size: 14px;
        font-weight: 750;
        letter-spacing: -0.012em;
      }
      #cc-main .pm__section:first-child .pm__section-desc-wrapper {
        margin-top: 5px;
      }
      #cc-main .pm__section:first-child .pm__section-desc {
        color: ${palette.mutedText};
        font-size: 12.5px;
        line-height: 1.5;
      }
      #cc-main .pm__section,
      #cc-main .pm__section--toggle {
        border-radius: calc(var(--cc-modal-border-radius) * 0.75);
      }
      #cc-main .pm__section--toggle {
        margin-bottom: 8px;
        background: transparent;
      }
      #cc-main .pm__section--toggle {
        border-color: color-mix(in srgb, ${palette.text} 10%, transparent);
      }
      #cc-main .pm__section--toggle .pm__section-title {
        min-height: 52px;
        border-color: color-mix(in srgb, ${palette.text} 10%, transparent);
        border-radius: 11px;
        background: ${palette.background};
        color: ${palette.text};
        font-size: 13.5px;
        font-weight: 700;
        line-height: 1.25;
        transition:
          background-color 180ms ease,
          border-color 180ms ease,
          border-radius 180ms ease;
      }
      #cc-main .pm__section--toggle .pm__section-title:hover {
        border-color: color-mix(in srgb, ${palette.text} 18%, transparent);
        background: ${palette.background};
      }
      #cc-main .pm__section--toggle .pm__section-desc-wrapper {
        border-color: transparent;
        border-radius: 0 0 11px 11px;
        background: ${palette.background};
        display: flex !important;
        max-height: 0;
        opacity: 0;
        overflow: hidden;
        pointer-events: none;
        transform: translateY(-4px);
        transform-origin: top;
        transition:
          max-height 320ms cubic-bezier(0.22, 1, 0.36, 1),
          opacity 180ms ease,
          transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
          border-color 180ms ease;
        visibility: hidden;
      }
      #cc-main .pm__section--toggle.is-expanded .pm__section-desc-wrapper {
        border-color: color-mix(in srgb, ${palette.text} 10%, transparent);
        max-height: 420px;
        opacity: 1;
        pointer-events: auto;
        transform: translateY(0);
        visibility: visible;
      }
      #cc-main .pm__section--expandable .pm__section-arrow {
        background: ${palette.background} !important;
        color: ${palette.mutedText} !important;
        transition:
          background-color 180ms ease,
          transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      #cc-main .pm__section--expandable .pm__section-arrow svg {
        stroke: ${palette.mutedText} !important;
        transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      #cc-main .pm__section-title {
        font-weight: 700;
      }
      #cc-main .pm__section-desc-wrapper {
        color: ${palette.mutedText};
        font-size: 13px;
        line-height: 1.55;
      }
      #cc-main .pm__badge {
        min-width: 25px;
        height: 25px;
        padding: 0 8px;
        border: 1px solid color-mix(in srgb, ${palette.primary} 12%, transparent);
        background: ${palette.background};
        color: ${palette.secondaryText};
        font-size: 11px;
        font-weight: 800;
      }
      #cc-main .section__toggle-wrapper {
        border-radius: 999px;
      }
      #cc-main .toggle__icon {
        box-shadow:
          inset 0 0 0 1px color-mix(in srgb, ${palette.text} 12%, transparent),
          0 2px 6px color-mix(in srgb, ${palette.text} 12%, transparent);
      }
      #cc-main .pm__footer {
        gap: 8px;
        padding: 20px 18px calc(16px + env(safe-area-inset-bottom));
        border-top: 0 !important;
        background: ${palette.background};
        box-shadow: none;
      }
      #cc-main .pm__btn {
        min-height: 42px;
        padding: 9px 12px;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.15;
        white-space: normal;
      }
      #cc-main .pm__btn:not(.pm__btn--secondary) {
        box-shadow: none;
      }
      #cc-main .pm__btn[data-role="necessary"] {
        box-shadow: none;
      }
      @media (min-width: 641px) {
        #cc-main .pm__footer {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        #cc-main .pm__btn-group {
          display: contents;
        }
      }
      @media (max-width: 640px) {
        #cc-main .cm {
          max-width: none;
        }
        #cc-main .cm--bar,
        #cc-main .cm--box,
        #cc-main .cm--box.cm--bottom.cm--left,
        #cc-main .cm--box.cm--bottom.cm--right,
        #cc-main .cm--box.cm--bottom.cm--center {
          left: 50% !important;
          right: auto !important;
          width: min(320px, calc(100vw - 28px)) !important;
          max-width: min(320px, calc(100vw - 28px)) !important;
          transform: translate(-50%, 100%);
        }
        #cc-main .cm--bar.cm--top {
          transform: translateY(-100%);
        }
        .show--consent #cc-main .cc--anim .cm--bar {
          transform: translate(-50%, 0) !important;
        }
        .show--consent #cc-main .cc--anim .cm--box {
          transform: translate(-50%, 0) !important;
        }
        #cc-main .cm--bar .cm__body,
        #cc-main .cm--bar.cm--inline .cm__body {
          align-items: stretch;
          flex-direction: column;
          gap: 0;
          padding: 0;
        }
        #cc-main .cm--bar .cm__texts {
          padding: 16px 16px 2px;
        }
        #cc-main .cm__texts {
          padding: 16px 16px 2px;
        }
        #cc-main .cm__title {
          font-size: 17px;
          gap: 8px;
          line-height: 1.15;
        }
        #cc-main .cm__title::before {
          width: 22px;
          height: 22px;
        }
        #cc-main .cm__desc {
          margin-top: 8px;
          font-size: 13px;
          line-height: 1.35;
        }
        #cc-main .cm__btns {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
          gap: 10px !important;
          padding: 12px 16px 14px;
        }
        #cc-main .cm__footer {
          padding: 0 16px 10px;
        }
        #cc-main .cm__footer a,
        #cc-main .cp__attribution a {
          font-size: 10px;
        }
        #cc-main .cm--bar .cm__btns {
          flex: none;
          width: 100% !important;
          padding: 12px 16px 14px;
        }
        html[data-consentpop-mode="opt-out"] #cc-main .cm__btns,
        html[data-consentpop-mode="opt-out"] #cc-main .cm--bar .cm__btns {
          align-content: start !important;
          align-items: stretch !important;
          flex: none !important;
          flex-basis: auto !important;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
          grid-template-rows: none !important;
          height: auto !important;
          width: 100% !important;
        }
        #cc-main .cm--bar .cm__btn {
          height: 38px;
          min-height: 38px;
        }
        #cc-main .cm__btn-group {
          display: grid !important;
          gap: 10px !important;
          grid-auto-columns: minmax(0, 1fr);
          grid-auto-flow: column;
          width: 100%;
        }
        #cc-main .pm__footer,
        #cc-main .pm__btn-group {
          gap: 10px !important;
        }
        #cc-main .cm__btn + .cm__btn {
          margin: 0 !important;
        }
        #cc-main .cm__btn,
        #cc-main .pm__btn {
          min-height: 38px;
          font-size: 14px;
          line-height: 1.15;
          min-width: 0;
          white-space: normal;
        }
      }
      @media (max-width: 380px) {
        #cc-main .cm__btns,
        #cc-main .cm--bar .cm__btns {
          grid-template-columns: 1fr !important;
        }
      }
      ${this.buildConsentActionStyles(preset, palette)}
      ${this.buildResponsiveStyles()}
      ${this.buildConsentLayoutStyles()}
      ${this.buildDensityStyles()}
      ${this.buildThemeFormatStyles(preset, palette)}
      ${buildCompactAppearanceStyles()}
      ${this.config.customCss || ""}
    `;
  }

  private buildThemeFormatStyles(_preset: ThemePreset, palette: ThemePalette): string {
    return buildSimpleAppearanceStyles(palette, this.config.layout === "bar");
  }

  private buildConsentLayoutStyles(): string {
    return `
      #cc-main .cm__btn[data-role="show"] {
        align-self: stretch;
        justify-self: stretch;
        order: 3;
        width: 100% !important;
        min-height: 44px !important;
        padding: 0 12px;
        border: 1px solid var(--cc-btn-secondary-border-color) !important;
        background: var(--cc-btn-secondary-bg) !important;
        box-shadow: none !important;
        color: var(--cc-btn-secondary-color) !important;
        font-size: 13px;
        font-weight: 700;
      }
      #cc-main .cm--bar .cm__btns {
        align-items: stretch;
        display: flex;
        flex: 0 0 372px;
        flex-direction: column !important;
        gap: 10px !important;
        padding: 0;
        width: 372px !important;
      }
      #cc-main .cm--bar .cm__btn-group {
        display: grid !important;
        gap: 10px !important;
        min-width: 0;
      }
      #cc-main .cm--bar .cm__btn-group:first-child {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      #cc-main .cm--bar .cm__btn-group:last-child {
        grid-template-columns: minmax(0, 1fr);
      }
      #cc-main .cm--bar .cm__btn {
        height: auto;
        min-height: 48px !important;
      }
      #cc-main .cm--box .cm__btns {
        display: flex;
        flex-direction: column;
        gap: 10px !important;
      }
      #cc-main .cm--box .cm__btn-group:first-child {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      #cc-main .cm--box .cm__btn-group:last-child {
        grid-template-columns: minmax(0, 1fr);
      }
      @media (max-width: 860px) {
        #cc-main .cm--bar .cm__btns {
          align-self: stretch;
          flex: none;
          flex-direction: column !important;
          padding: 12px 18px 16px;
          width: 100% !important;
        }
        #cc-main .cm--bar .cm__btn-group:first-child,
        #cc-main .cm--bar .cm__btn-group:last-child {
          width: 100%;
        }
      }
      @media (max-width: 640px) {
        #cc-main .cm--bar .cm__btns,
        #cc-main .cm--box .cm__btns {
          gap: 8px !important;
          padding: 12px 16px 14px !important;
        }
      }
    `;
  }

  private buildDensityStyles(): string {
    return `
      #${PREFERENCES_LAUNCHER_ID} {
        position: fixed;
        z-index: 2147483000;
        right: max(16px, env(safe-area-inset-right));
        bottom: max(16px, env(safe-area-inset-bottom));
        display: inline-flex;
        min-width: 44px;
        min-height: 44px;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border: 1px solid var(--cc-separator-border-color);
        border-radius: 999px;
        padding: 10px 14px;
        background: var(--cc-bg);
        box-shadow: 0 10px 28px rgba(15, 15, 12, 0.18);
        color: var(--cc-primary-color);
        font-family: inherit;
        font-weight: 700;
        font-size: 12px;
        line-height: 1;
        cursor: pointer;
      }
      #${PREFERENCES_LAUNCHER_ID} .cp__icon {
        display: block;
        width: 18px;
        height: 18px;
      }
      #${PREFERENCES_LAUNCHER_ID}[data-position="top-left"] {
        top: max(16px, env(safe-area-inset-top));
        right: auto;
        bottom: auto;
        left: max(16px, env(safe-area-inset-left));
      }
      #${PREFERENCES_LAUNCHER_ID}[data-position="top-right"] {
        top: max(16px, env(safe-area-inset-top));
        bottom: auto;
      }
      #${PREFERENCES_LAUNCHER_ID}[data-position="bottom-left"] {
        right: auto;
        left: max(16px, env(safe-area-inset-left));
      }
      #${PREFERENCES_LAUNCHER_ID}[data-style="mini"] {
        padding: 10px;
      }
      #${PREFERENCES_LAUNCHER_ID}[data-style="mini"] .cp__preferences-launcher-label {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
      #${PREFERENCES_LAUNCHER_ID}[hidden],
      .show--consent #${PREFERENCES_LAUNCHER_ID},
      .show--preferences #${PREFERENCES_LAUNCHER_ID} {
        display: none;
      }
      html[data-consentpop-density="compact"] #cc-main .cm--bar {
        width: min(660px, calc(100vw - 32px)) !important;
        max-width: min(660px, calc(100vw - 32px)) !important;
      }
      html[data-consentpop-density="compact"] #cc-main .cm--box {
        width: min(360px, calc(100vw - 28px)) !important;
        max-width: min(360px, calc(100vw - 28px)) !important;
      }
      html[data-consentpop-density="compact"] #cc-main .cm__texts {
        padding: 14px 16px 0;
      }
      html[data-consentpop-density="compact"] #cc-main .cm--bar .cm__texts {
        padding: 0;
      }
      html[data-consentpop-density="compact"] #cc-main .cm__title {
        font-size: 16px;
      }
      html[data-consentpop-density="compact"] #cc-main .cm__title::before {
        width: 20px;
        height: 20px;
      }
      html[data-consentpop-density="compact"] #cc-main .cm__desc {
        margin-top: 7px;
        font-size: 13px;
        line-height: 1.4;
      }
      html[data-consentpop-density="compact"] #cc-main .cm__btns,
      html[data-consentpop-density="compact"] #cc-main .cm--bar .cm__btns {
        gap: 8px !important;
        padding: 18px 16px 14px !important;
      }
      html[data-consentpop-density="compact"] #cc-main .cm--bar .cm__btns {
        width: 330px !important;
        padding: 0;
      }
      html[data-consentpop-density="compact"] #cc-main .cm__btn {
        min-height: 44px !important;
        padding: 8px 10px;
        font-size: 13px;
      }
      /* Emit this last so no theme or breakpoint can shrink an actionable control. */
      #cc-main .cm__btn[data-role="all"],
      #cc-main .cm__btn[data-role="necessary"],
      #cc-main .cm__btn[data-role="show"],
      #cc-main .pm__btn {
        min-height: 44px !important;
      }
      #cc-main .pm__close-btn {
        width: 44px !important;
        height: 44px !important;
        min-width: 44px !important;
        min-height: 44px !important;
      }
      @media (max-width: 640px) {
        html[data-consentpop-density="compact"] #cc-main .cm--bar {
          width: min(360px, calc(100vw - 24px)) !important;
          max-width: min(360px, calc(100vw - 24px)) !important;
        }
        #${PREFERENCES_LAUNCHER_ID} {
          padding: 10px;
        }
        #${PREFERENCES_LAUNCHER_ID}[data-position="top-left"] {
          top: max(12px, env(safe-area-inset-top));
          left: max(12px, env(safe-area-inset-left));
        }
        #${PREFERENCES_LAUNCHER_ID}[data-position="top-right"] {
          top: max(12px, env(safe-area-inset-top));
          right: max(12px, env(safe-area-inset-right));
        }
        #${PREFERENCES_LAUNCHER_ID}[data-position="bottom-left"] {
          bottom: max(12px, env(safe-area-inset-bottom));
          left: max(12px, env(safe-area-inset-left));
        }
        #${PREFERENCES_LAUNCHER_ID}[data-position="bottom-right"] {
          right: max(12px, env(safe-area-inset-right));
          bottom: max(12px, env(safe-area-inset-bottom));
        }
        #${PREFERENCES_LAUNCHER_ID} .cp__preferences-launcher-label {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        html[data-consentpop-density="compact"] #cc-main .cm--bar .cm__btns {
          width: 100% !important;
          padding: 18px 16px 14px !important;
        }
      }
    `;
  }

  /** Shared responsive geometry, touch targets, and viewport safety. */
  private buildResponsiveStyles(): string {
    return `
      #cc-main .pm {
        isolation: isolate;
        height: auto;
        width: min(560px, calc(100vw - 40px));
        max-height: min(640px, calc(100vh - 40px));
        max-height: min(640px, calc(100dvh - 40px));
        border-radius: var(--cc-modal-border-radius);
        background: var(--cc-bg);
      }
      #cc-main .pm::before {
        content: none;
      }
      #cc-main .pm__body {
        min-height: 0;
        overscroll-behavior: contain;
      }
      #cc-main .pm__close-btn {
        width: 38px;
        height: 38px;
        min-width: 38px;
        min-height: 38px;
      }
      @media (max-width: 860px) {
        #cc-main .cm--bar {
          width: min(720px, calc(100vw - 32px)) !important;
          max-width: min(720px, calc(100vw - 32px)) !important;
        }
        #cc-main .cm--bar .cm__body,
        #cc-main .cm--bar.cm--inline .cm__body {
          align-items: stretch;
          flex-direction: column;
          gap: 0;
          min-height: auto;
          padding: 0;
        }
        #cc-main .cm--bar .cm__texts {
          align-items: flex-start;
          flex-direction: column;
          gap: 0;
          padding: 16px 18px 2px;
        }



        #cc-main .cm--bar .cm__btns {
          align-self: stretch;
          flex: none;
          width: 100% !important;
          padding: 12px 18px 16px;
        }
        #cc-main .cm--bar .cm__footer {
          padding: 0 18px 12px;
        }
      }
      @media (max-width: 640px) {
        :root {
          --cc-modal-margin: max(8px, env(safe-area-inset-bottom));
        }
        #cc-main .cm,
        #cc-main .cm--bar,
        #cc-main .cm--box,
        #cc-main .cm--cloud,
        #cc-main .cm--box.cm--bottom.cm--left,
        #cc-main .cm--box.cm--bottom.cm--right,
        #cc-main .cm--box.cm--bottom.cm--center {
          left: 50% !important;
          right: auto !important;
          width: min(360px, calc(100vw - 24px)) !important;
          max-width: min(360px, calc(100vw - 24px)) !important;
          max-height: calc(100dvh - 16px);
          transform: translate(-50%, 100%);
        }
        #cc-main .cm--bar.cm--top {
          top: max(8px, env(safe-area-inset-top));
          transform: translate(-50%, -100%);
        }
        #cc-main .cm--bar.cm--bottom,
        #cc-main .cm--box.cm--bottom {
          bottom: max(8px, env(safe-area-inset-bottom));
        }
        .show--consent #cc-main .cc--anim .cm,
        .show--consent #cc-main .cc--anim .cm--bar,
        .show--consent #cc-main .cc--anim .cm--box,
        .show--consent #cc-main .cc--anim .cm--box.cm--center {
          transform: translate(-50%, 0) !important;
        }
        #cc-main .cm__body,
        #cc-main .cm--bar .cm__body,
        #cc-main .cm--bar.cm--inline .cm__body,
        #cc-main .cm--cloud .cm__body {
          align-items: stretch;
          flex-direction: column !important;
          gap: 0;
          overflow: auto;
          padding: 0 !important;
        }
        #cc-main .cm__texts,
        #cc-main .cm--bar .cm__texts {
          padding: 16px 16px 2px !important;
        }
        #cc-main .cm__title {
          font-size: clamp(16px, 4.5vw, 18px);
          gap: 8px;
          line-height: 1.2;
        }
        #cc-main .cm__title::before {
          width: 24px;
          height: 24px;
        }
        #cc-main .cm__desc {
          margin-top: 8px;
          font-size: 13px;
          line-height: 1.45;
          overflow-wrap: anywhere;
          white-space: normal;
        }



        #cc-main .cm__btns,
        #cc-main .cm--bar .cm__btns {
          gap: 8px !important;
          padding: 12px 16px 14px !important;
        }
        #cc-main .cm__btn,
        #cc-main .cm--bar .cm__btn,
        #cc-main .pm__btn {
          height: auto;
          min-height: 44px !important;
          padding: 8px 10px;
          font-size: 14px;
          line-height: 1.2;
          white-space: normal;
        }
        #cc-main .cm__footer,
        #cc-main .cm--bar .cm__footer {
          padding: 0 16px 12px;
        }
        #cc-main .pm {
          top: max(8px, env(safe-area-inset-top));
          right: auto;
          bottom: auto;
          left: 50%;
          width: calc(100vw - 16px);
          height: auto;
          max-height: calc(100vh - 16px);
          max-height: calc(100dvh - 16px);
          border-radius: 16px;
          transform: translate(-50%, 1.6em);
        }
        .show--preferences #cc-main .cc--anim .pm {
          transform: translate(-50%, 0) !important;
        }
        #cc-main .pm__header {
          min-height: 58px;
          padding: 11px 14px !important;
        }
        #cc-main .pm__title {
          gap: 8px;
          font-size: 16px;
        }
        #cc-main .pm__title::before {
          width: 21px;
          height: 21px;
        }
        #cc-main .pm__body {
          padding: 12px 12px 14px !important;
          scroll-padding: 12px;
        }
        #cc-main .pm__section:first-child {
          margin-bottom: 10px;
          padding: 0 2px 10px;
        }
        #cc-main .pm__section:first-child .pm__section-title {
          font-size: 15px;
        }
        #cc-main .pm__section--toggle {
          margin-bottom: 8px;
        }
        #cc-main .pm__section--toggle .pm__section-title {
          min-height: 50px;
          padding-top: 10px;
          padding-bottom: 10px;
        }
        #cc-main .pm__footer {
          gap: 8px !important;
          padding: 10px 12px calc(10px + env(safe-area-inset-bottom)) !important;
        }
        #cc-main .pm__btn {
          min-height: 40px !important;
          padding: 8px 10px;
          font-size: 13px;
        }
        #cc-main .pm__footer,
        #cc-main .pm__btn-group {
          width: 100%;
        }
        #cc-main .pm__footer {
          align-items: stretch;
          flex-direction: column;
        }
        #cc-main .pm__btn-group {
          width: 100%;
        }
      }
      @media (max-width: 420px) {
        #cc-main .pm {
          top: max(6px, env(safe-area-inset-top));
          width: calc(100vw - 12px);
          max-height: calc(100vh - 12px);
          max-height: calc(100dvh - 12px);
          border-radius: 16px;
        }
        #cc-main .pm__header {
          min-height: 60px;
          padding: 12px 14px !important;
        }
        #cc-main .pm__close-btn {
          width: 36px;
          height: 36px;
          min-width: 36px;
          min-height: 36px;
        }
        #cc-main .pm__body {
          padding: 10px !important;
        }
        #cc-main .pm__section--toggle .pm__section-title {
          padding-right: 76px;
        }
      }
      @media (min-width: 641px) and (max-height: 620px) {
        #cc-main .pm {
          max-height: calc(100vh - 24px);
          max-height: calc(100dvh - 24px);
        }
        #cc-main .pm__header {
          min-height: 64px;
          padding-top: 13px;
          padding-bottom: 13px;
        }
        #cc-main .pm__body {
          padding-top: 14px;
          padding-bottom: 14px;
        }
        #cc-main .pm__footer {
          padding-top: 12px;
          padding-bottom: 12px;
        }
      }
      @media (max-width: 340px) {
        #cc-main .cm__btn-group {
          grid-auto-flow: row !important;
          grid-template-columns: 1fr !important;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        #cc-main *,
        #cc-main *::before,
        #cc-main *::after {
          scroll-behavior: auto !important;
          transition-duration: 0.01ms !important;
        }
      }
    `;
  }

  /** Shared first-layer action colors for box and bar. */
  private buildConsentActionStyles(
    preset: ThemePreset,
    palette: ThemePalette,
  ): string {
    const scope = `html[data-consentpop-theme="${preset}"] #cc-main`;
    const accent = palette.primary;
    const actionBase = `
      ${scope} .cm__btn[data-role="all"] {
        background: ${accent} !important;
        border-color: ${accent} !important;
        color: ${palette.primaryText} !important;
      }
      ${scope} .cm__btn[data-role="all"]:hover {
        background: ${palette.primaryHover} !important;
        border-color: ${palette.primaryHover} !important;
        color: ${palette.primaryText} !important;
      }
    `;

    return actionBase;
  }
}
